import type { PlatformAccentBounds } from '../organicPlatformAccent';
import { drawTidewendTownMaterial } from '../tidewend/townMaterials';
import { drawProfiledTownMaterial, type TownMaterialProfile } from '../wickhaven/townMaterials';
import type { TownMaterialGraphics } from './townSurfaceMaterials';

const WOODLAND: TownMaterialProfile = {
  quay: {
    face: 0x465651, deep: 0x273b3b, mortar: 0x2b3b3b,
    stones: [0x5a6b61, 0x53675f, 0x4c605b, 0x657569],
    cap: 0x839084, edge: 0xb1b7a2, wet: 0x38534f,
  },
  timber: { face: 0x65503f, shade: 0x393b36, grain: 0x8c7454,
    cap: 0xaf9770, edge: 0xd1bd93, iron: 0x344448 },
};
const COASTAL: TownMaterialProfile = {
  quay: {
    face: 0x374457, deep: 0x222b3b, mortar: 0x252d3e,
    stones: [0x4a566a, 0x424d61, 0x39465a, 0x525c6d],
    cap: 0x737d8f, edge: 0xa5aabd, wet: 0x334956,
  },
  timber: { face: 0x57443f, shade: 0x352d32, grain: 0x7d6151,
    cap: 0xa78664, edge: 0xc7ab87, iron: 0x343b4b },
};

function portProfile(base: TownMaterialProfile, coastal: boolean): TownMaterialProfile {
  return {
    ...base,
    harbor: {
      x: 900, width: 650,
      style: {
        palette: {
          waterTop: coastal ? 0x343952 : 0x36595c, waterDeep: coastal ? 0x161c30 : 0x192f38,
          waterLight: coastal ? 0x8996b5 : 0x91b4ae,
          wood: base.timber.face, woodShade: base.timber.shade, woodEdge: base.timber.edge,
          stone: base.quay.face, stoneEdge: base.quay.cap, iron: base.timber.iron,
          rope: 0xab997b, amber: coastal ? 0xc79c60 : 0xd4ab70,
          hull: coastal ? 0x5b4851 : 0x405b58, cabin: coastal ? 0x716373 : 0x7d8069,
        },
        dressing: {
          deckHeight: 28, abutmentWidth: 32, pileOffsets: [70, 200, 350, 490, 580],
          reflectionCount: 20, lampOffset: 430, ferry: { x: 360, y: 40, width: 210, height: 96 },
        },
      },
    },
  };
}

/** Only this adapter selects maps. Low-level builders remain reusable with other profile data. */
export const TOWN_MATERIAL_PROFILES: Readonly<Record<string, TownMaterialProfile>> = {
  wickhaven: WOODLAND,
  wickhaven_port: portProfile(WOODLAND, false),
  tidewend_port: portProfile(COASTAL, true),
};

/** True means the town material owns the complete surface in the caller's existing Graphics. */
export function drawTownPlatformMaterial(
  graphics: TownMaterialGraphics, levelId: string, platform: PlatformAccentBounds,
  isLedge: boolean, groundY: number,
): boolean {
  if (levelId === 'tidewend') {
    drawTidewendTownMaterial(graphics, platform, isLedge, groundY);
    return true;
  }
  if (!Object.prototype.hasOwnProperty.call(TOWN_MATERIAL_PROFILES, levelId)) return false;
  drawProfiledTownMaterial(graphics, platform, isLedge, groundY, TOWN_MATERIAL_PROFILES[levelId]);
  return true;
}
